import React, { useEffect, useState } from "react";
import { TextField, Button, Box, Autocomplete, Chip, Typography } from "@mui/material";
import {useAuth} from "../components/TokenHandler";

const ModifyUserForm = ({ user, setUser,onClose, refreshUsers  }) => {

    const [availableRoles, setAvailableRoles] = useState([]);
    const [assignedRoles, setAssignedRoles] = useState([]);

    const {
        keycloak: kc,
        authenticated,
        infoMessage,
        setInfoMessage,
        givenName,
        email,
        roles,
    } = useAuth();

    useEffect(() => {
        // Fetch available roles
        const fetchAvailableRoles = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/get-keycloak-roles`, {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${kc.token}`, // Include the token in the Authorization header
                        "Content-Type": "application/json"
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    setAvailableRoles(data);
                } else {
                    console.error("Failed to fetch available roles:", response.statusText);
                }
            } catch (error) {
                console.error("Error fetching available roles:", error);
            }
        };

        // Fetch roles assigned to the user
        const fetchAssignedRoles = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/get-keycloak-users-roles?email=${user.email}`, {
                        method: "GET",
                        headers: {
                            "Authorization": `Bearer ${kc.token}`, // Include the token in the Authorization header
                            "Content-Type": "application/json"
                        },
                    });
                if (response.ok) {
                    const data = await response.json();
                    const roles = data.roles.clientMappings["consent-user-control"].mappings.map(
                        ({ id, name, description }) => ({ id, name, description })
                    );
                    setAssignedRoles(roles);
                } else {
                    console.error("Failed to fetch assigned roles:", response.statusText);
                }
            } catch (error) {
                console.error("Error fetching assigned roles:", error);
            }
        };

        if (user.email) {
            fetchAvailableRoles();
            fetchAssignedRoles();
        }
    }, [user.email]);

    const handleRoleChange = (event, value) => {
        setAssignedRoles(value);
    };


    const handleChange = (field, value) => {
        setUser((prev) => ({ ...prev, [field]: value }));
    };

    const handleModify = () => {
        console.log("User Modified:", user);
        // Add API call for modifying a user here
    };

    const handleSuspendUser = async () => {
        try {
            const suspendResponse = await fetch(`${process.env.REACT_APP_API_URL}/suspend-keycloak-user`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${kc.token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ userEmail: user.email }),
            });

            if (suspendResponse.ok) {
                const suspendData = await suspendResponse.json();
                console.log("Suspend Success:", suspendData);

                const updateResponse = await fetch(`${process.env.REACT_APP_API_URL}/update-user`, {
                    method: "PUT",
                    headers: {
                        "Authorization": `Bearer ${kc.token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ id: user.id, status: false }),
                });

                if (updateResponse.ok) {
                    console.log("User status updated successfully");
                    setUser((prev) => ({
                        ...prev,
                        status: false,
                    }));
                } else {
                    console.error("Failed to update user status:", updateResponse.statusText);
                }
            } else {
                console.error("Failed to suspend user:", suspendResponse.statusText);
            }
        } catch (error) {
            console.error("Error suspending user:", error);
        }
    };

    const handleEnableUser = async () => {
        try {
            const enableResponse = await fetch(`${process.env.REACT_APP_API_URL}/enable-keycloak-user`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${kc.token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ userEmail: user.email }),
            });

            if (enableResponse.ok) {
                const enableData = await enableResponse.json();
                console.log("Enable Success:", enableData);

                const updateResponse = await fetch(`${process.env.REACT_APP_API_URL}/update-user`, {
                    method: "PUT",
                    headers: {
                        "Authorization": `Bearer ${kc.token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ id: user.id, status: true }),
                });

                if (updateResponse.ok) {
                    console.log("User status updated successfully");
                    setUser((prev) => ({
                        ...prev,
                        status: true,
                    }));
                } else {
                    console.error("Failed to update user status:", updateResponse.statusText);
                }
            } else {
                console.error("Failed to enable user:", enableResponse.statusText);
            }
        } catch (error) {
            console.error("Error enabling user:", error);
        }
    };

    const handleDeleteUser = async () => {
        try {
            // First API call to delete user in Keycloak
            const deleteResponse = await fetch(`${process.env.REACT_APP_API_URL}/delete-keycloak-user`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${kc.token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ userEmail: user.email }),
            });

            if (deleteResponse.ok) {
                const deleteData = await deleteResponse.json();
                console.log("Keycloak user deleted successfully:", deleteData);

                // Second API call to delete the user from the database
                const deleteDbResponse = await fetch(`${process.env.REACT_APP_API_URL}/delete-user`, {
                    method: "DELETE",
                    headers: {
                        "Authorization": `Bearer ${kc.token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ userEmail: user.email }),
                });

                if (deleteDbResponse.ok) {
                    const deleteDbData = await deleteDbResponse.json();
                    console.log("User deleted from database successfully:", deleteDbData);

                    onClose();
                    refreshUsers();
                    // Add any additional UI updates here if needed
                } else {
                    console.error("Failed to delete user from database:", deleteDbResponse.statusText);
                }
            } else {
                console.error("Failed to delete user in Keycloak:", deleteResponse.statusText);
            }
        } catch (error) {
            console.error("Error deleting user:", error);
        }
    };


    const handleResetPassword = async () => {
        try {
            const resetResponse = await fetch(`${process.env.REACT_APP_API_URL}/reset_keycloak_user_password`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${kc.token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ userEmail: user.email }),
            });

            if (resetResponse.ok) {
                const resetData = await resetResponse.json();
                console.log("Password Reset Success:", resetData);
            } else {
                console.error("Failed to reset password:", resetResponse.statusText);
            }
        } catch (error) {
            console.error("Error resetting password:", error);
        }
    };

    const handleAssignRole = async () => {
        try {
            // Prepare payload for assign-keycloak-role
            const roleDataPayload = {
                userEmail: user.email,
                roleData: assignedRoles, // Assigned roles as roleData
            };

            // Make the API call to assign roles
            const assignResponse = await fetch(`${process.env.REACT_APP_API_URL}/assign-keycloak-role`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${kc.token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(roleDataPayload),
            });

            if (assignResponse.ok) {
                const assignData = await assignResponse.json();
                console.log("Assign Roles Success:", assignData);

                // Prepare payload for update-user
                const roleNames = assignedRoles.map((role) => role.name);
                const attributes = roleNames.reduce((acc, role) => {
                    acc[role] = ""; // Set all values to blank
                    return acc;
                }, {});

                const updateUserPayload = {
                    id: user.id,
                    roles: roleNames, // List of role names as JSONB
                    attributes: attributes, // Role names as keys, values blank
                };

                // Make the API call to update user
                const updateResponse = await fetch(`${process.env.REACT_APP_API_URL}/update-user`, {
                    method: "PUT",
                    headers: {
                        "Authorization": `Bearer ${kc.token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(updateUserPayload),
                });

                if (updateResponse.ok) {
                    const updateData = await updateResponse.json();
                    console.log("User updated successfully:", updateData);
                } else {
                    console.error("Failed to update user:", updateResponse.statusText);
                }
            } else {
                console.error("Failed to assign roles:", assignResponse.statusText);
            }
        } catch (error) {
            console.error("Error assigning roles:", error);
        }
    };

    return (
        <Box>
            {/* User Information and Suspend/Enable Section */}
            <Box
                sx={{
                    backgroundColor: "#f5f5f5",
                    padding: 2,
                    borderRadius: 2,
                    marginBottom: 2, // Spacing between the two grey boxes
                }}
            >
                {/* User Information */}
                <Box sx={{ marginBottom: 1 }}>
                    <Typography sx={{ textAlign: "left", fontWeight: "bold" }}>
                        {user.first_name} {user.last_name}
                    </Typography>
                </Box>
                <Typography sx={{ textAlign: "left", marginBottom: 2 }}>{user.email}</Typography>

                {/* Suspend/Enable and Reset Password Buttons */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Button
                        variant="outlined"
                        color={user.status ? "success" : "error"}
                        onClick={user.status ? handleSuspendUser : handleEnableUser}
                    >
                        {user.status ? "User Active" : "User Suspended"}
                    </Button>

                    {user.status && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleResetPassword} // Call the new function
                        >
                            Reset Password
                        </Button>
                    )}

                    {/* Delete User Button */}
                    {user.status === false && (
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleDeleteUser}
                        >
                            Delete User
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Multiselect Roles and Modify Section */}
            <Box
                sx={{
                    backgroundColor: "#f5f5f5",
                    padding: 2,
                    borderRadius: 2,
                }}
            >
                {/* Multiselect Roles */}
                <Autocomplete
                    multiple
                    options={availableRoles}
                    value={assignedRoles}
                    getOptionLabel={(option) => option.name} // Show only the name in the selected value
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(event, value) => setAssignedRoles(value)}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            <Box>
                                <Typography sx={{ fontWeight: "bold" }}>{option.name}</Typography>
                                <Typography sx={{ fontSize: "0.875rem" }}>{option.description}</Typography>
                            </Box>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Assigned Roles"
                            placeholder="Select roles"
                        />
                    )}
                    sx={{ marginBottom: 2 }} // Spacing within the grey box
                />

                {/* Modify/Assign Roles Button */}
                <Button variant="contained" color="primary" onClick={handleAssignRole}>
                    Assign Role
                </Button>
            </Box>
        </Box>

    );
};

export default ModifyUserForm;
